import { AxiosResponse } from 'axios';
import { AuthStoreType } from '../store/stores/auth.store';
import { handleControlTowerRedirect } from './handleControlTowerRedirect';
import { LoginResponseData } from '@interfaces/Login.interface';
import { authApi } from '@/api-client/auth';
import {
  isTrustedOAuthAuthorizeReturnUrl,
  readPostLoginRedirectFromSearch
} from './oauthPostLoginRedirect';

const ACTIVE_LOGIN_STORAGE_KEYS = [
  'token',
  'loggedin_tenant_company_uuid',
  'loggedin_tenant_company_name',
  'loggedin_tenant_company_brand_color',
  'email-verification-token'
] as const;

const LEGACY_LOGIN_STORAGE_KEYS = [
  'loggedin_user',
  'loggedin_token',
  'loggedin_needs_kyb',
  'loggedin_kyb_status',
  'loggedin_tenant_company_logo',
  'loggedin_tenant_company_icon',
  'loggedin_tenant_company_id',
  'loggedin_tenant_company_is_internal',
  'loggedin_tenant_is_master_tenant'
] as const;

export function removeLegacyLoginStorageKeys(): void {
  LEGACY_LOGIN_STORAGE_KEYS.forEach((key) => localStorage.removeItem(key));
}

function persistTenantCompanyContext(company: LoginResponseData['tenant']['company']): void {
  if (!company) return;

  localStorage.setItem('loggedin_tenant_company_uuid', company.uuid ?? '');
  localStorage.setItem('loggedin_tenant_company_name', company.name ?? '');
  localStorage.setItem(
    'loggedin_tenant_company_brand_color',
    company.main_color || ''
  );
}

/**
 * Handles the login response and stores all necessary data
 * Replicates the Laravel backend postLogin functionality
 */
export const handleLoginResponse = async (
  res: AxiosResponse<LoginResponseData>,
  authStore: AuthStoreType,
  setIsNotFrontEndUser?: (value: boolean) => void
): Promise<boolean | undefined> => {
  // Capture before setUser / async work: LoginJourney may client-navigate away from
  // /login and drop ?redirect= before this handler finishes.
  const capturedPostLoginRedirect = readPostLoginRedirectFromSearch();
  const data = res.data;

  // Check for email verification requirement
  if (data.message === 'email-verification-required') {
    // Store email verification token
    localStorage.setItem('email-verification-token', data.token);
    // Redirect to email verification page
    window.location.href = '/login-email-verification-send';
    return;
  }

  // Check if user is allowed to use frontend
  if (!data.is_front_end_user) {
    setIsNotFrontEndUser?.(true);
    return;
  }

  // Handle control tower redirect
  if (handleControlTowerRedirect(res)) return;

  if (data.token) {
    localStorage.setItem('token', data.token);
    persistTenantCompanyContext(data.tenant?.company);
    removeLegacyLoginStorageKeys();

    // Update auth store
    authStore.setToken(data.token);
    authStore.setUser(data.user);

    try {
      await authApi.establishPassportWebSession();
    } catch (e) {
      console.error('establishPassportWebSession failed:', e);
      clearStoredLoginData();
      authStore.setToken(null);
      authStore.setUser(null);
      throw e;
    }

    const oauthReturn =
      capturedPostLoginRedirect?.kind === 'oauth'
        ? capturedPostLoginRedirect.target
        : null;
    if (oauthReturn && isTrustedOAuthAuthorizeReturnUrl(oauthReturn)) {
      window.location.assign(oauthReturn);
      return true;
    }

    if (capturedPostLoginRedirect?.kind === 'portal') {
      window.location.href = capturedPostLoginRedirect.target;
      return true;
    }

    // Handle redirects based on company type
    if (data.tenant?.company?.is_internal === false) {
      window.location.href = '/flightdeck-passport';
    } else {
      window.location.href = '/dashboard';
    }

    return true; // Success
  } else {
    return false; // No token provided
  }
};

/**
 * Clears all stored login data
 */
export const clearStoredLoginData = () => {
  ACTIVE_LOGIN_STORAGE_KEYS.forEach((key) => localStorage.removeItem(key));
  removeLegacyLoginStorageKeys();

  // Clear company_uuid cookie
  document.cookie =
    'company_uuid=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT';
};

function portalLoginAbsoluteUrl(userUuid: string | undefined): string {
  const base = import.meta.env.VITE_APP_URL.replace(/\/+$/, '');
  const qs = userUuid ? `?uuid=${encodeURIComponent(userUuid)}` : '';
  return `${base}/login${qs}`;
}

function configuratorLogoutUrl(redirectAfter: string): string | null {
  const raw = import.meta.env.VITE_CONFIGURATOR_URL?.trim() ?? '';
  if (!raw) {
    if (import.meta.env.DEV) {
      console.warn(
        'VITE_CONFIGURATOR_URL is unset; PBv2 session cookies may remain after Portal logout.'
      );
    }
    return null;
  }
  const origin = raw.replace(/\/+$/, '');
  return `${origin}/auth/logout?redirect=${encodeURIComponent(redirectAfter)}`;
}

function redirectAfterLogout(companyUuid?: string): void {
  const loginAbsolute = portalLoginAbsoluteUrl(companyUuid);
  const viaConfigurator = configuratorLogoutUrl(loginAbsolute);
  if (viaConfigurator) {
    window.location.replace(viaConfigurator);
  } else {
    const path = `/login${companyUuid ? `?uuid=${companyUuid}` : ''}`;
    window.location.replace(path);
  }
}

/**
 * Logout function that clears all stored data and redirects to login
 */
export const logout = async (authStore: AuthStoreType) => {
  const user = authStore.user;
  const companyUuid = user?.company?.uuid;

  const clearLocalAuthState = () => {
    clearStoredLoginData();
    sessionStorage.clear();
    authStore.setToken(null);
    authStore.setUser(null);
  };

  try {
    try {
      await authApi.destroyPassportWebSession();
    } catch (e) {
      console.warn('destroyPassportWebSession failed:', e);
    }
    await authApi.logout();
    clearLocalAuthState();
    redirectAfterLogout(companyUuid ?? undefined);
  } catch (error) {
    console.error('Logout API call failed:', error);
    clearLocalAuthState();
    redirectAfterLogout(companyUuid ?? undefined);
  }
};
